import type { Degree, Distance, SpatialRectangle } from "@pdfking/math"
import { Max, Scale, Subtract, Total } from "@pdfking/math"
import { MM, ToPixel } from "@pdfking/quantity"
import type { PageShape } from "@pdfking/renderer-api"
import { GroupMatrix, HorizontalAlign, PlotFunction, VerticalAlign } from "@pdfking/renderer-api"
import type { StringGUID } from "@pdfking/tfunc"

import { PALETTE, TITLE } from "#constants"

import { AuthorName } from "../text/AuthorName"

// The spine in `box`: the primary, two spot bands across it near either end,
// and title and author along it when it is wide enough to carry them — down
// `box` when the book opens at the side, across it when `opening_top`.
// `short` is the shortest side of the cover's boards, which it is set by
export const DrawSpine = ({
  box,
  opening_top,
  seed,
  short,
}: {
  box: SpatialRectangle<Distance>
  opening_top: boolean
  seed: StringGUID
  short: Distance
}): PageShape[] => {
  const band = Scale(short, 0.05)
  const shapes: PageShape[] = [
    { enum: PlotFunction.PLOT_RECT, fill_color: PALETTE.primary, position: box },
    ...[0.1, 0.9].map((at): PageShape => ({
      enum: PlotFunction.PLOT_RECT,
      fill_color: PALETTE.spot,
      position: opening_top
        ? { ...box, width: band, x: Subtract(Total(box.x, Scale(box.width, at)), Scale(band, 0.5)) }
        : {
            ...box,
            height: band,
            y: Subtract(Total(box.y, Scale(box.height, at)), Scale(band, 0.5)),
          },
    })),
  ]
  const [along, across] = opening_top ? [box.width, box.height] : [box.height, box.width]
  const size = Scale(across, 0.42)
  if (size < Max(Scale(short, 0.01875), MM(1))) return shapes
  return [
    ...shapes,
    {
      enum: PlotFunction.PLOT_GROUP,
      // Read from the top down the spine, as a shelf shows it
      matrix: GroupMatrix({
        rotation: (opening_top ? 0 : -90) as Degree,
        translate: {
          x: Total(box.x, Scale(box.width, 0.5)),
          y: Total(box.y, Scale(box.height, 0.5)),
        },
      }),
      shapes: [
        {
          enum: PlotFunction.PLOT_TEXT,
          font_size: ToPixel(size),
          horizontal_align: HorizontalAlign.CENTER,
          position: { height: size, width: along, x: Scale(along, -0.5), y: Scale(size, -0.5) },
          text: `${TITLE.toUpperCase()}  ·  ${AuthorName(seed)}`,
          text_color: PALETTE.text,
          vertical_align: VerticalAlign.CENTER,
        },
      ],
    },
  ]
}
